import dayjs from 'dayjs'

import type {
  CarrierBrief,
  CarrierResponse,
} from '#/shared/api/generated/shipment/models'

export interface SidebarCarrierInsuranceStatus {
  isActive: boolean
  daysLeft: number
}

export interface SidebarCarrierInsurance {
  status?: SidebarCarrierInsuranceStatus
  expirationDate?: string
  autoLiability?: string
  cargo?: string
  cargoDeductible?: string
  fileUrl?: string
}

export interface SidebarCarrierInfo {
  name: string
  isAuthorized: boolean
  mc?: string
  dot?: string
  trucks?: number
  drivers?: number
  address?: string
  contactName?: string
  phone?: string
  email?: string
  insurance?: SidebarCarrierInsurance
}

function str(value: unknown): string | undefined {
  if (value == null) return undefined
  const text = String(value).trim()
  return text.length > 0 ? text : undefined
}

function num(value: unknown): number | undefined {
  if (value == null || value === '') return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

/** `mc_number` sometimes arrives prefixed ("MC-812893"); the label re-adds it. */
function stripMcPrefix(value: string | undefined): string | undefined {
  return value?.replace(/^mc[-\s]*/i, '')
}

interface FmcsaFields {
  isAuthorized: boolean
  mcNumber?: string
  dotNumber?: string
  trucks?: number
  drivers?: number
}

function buildFmcsaFields(detail: CarrierResponse | undefined): FmcsaFields {
  const fmcsa: Record<string, unknown> = detail?.fmcsa_info ?? {}
  return {
    isAuthorized: str(fmcsa['allowedToOperate']) === 'Y',
    mcNumber: str(fmcsa['mcNumber']),
    dotNumber: str(fmcsa['usDotNumber']),
    trucks: num(fmcsa['totalPowerUnits']),
    drivers: num(fmcsa['totalDrivers']),
  }
}

function buildAddress(detail: CarrierResponse): string | undefined {
  const region = [str(detail.state), str(detail.zip)].filter(Boolean).join(' ')
  const joined = [str(detail.address), str(detail.city), region]
    .filter(Boolean)
    .join(', ')
  return joined.length > 0 ? joined : undefined
}

function formatCompactUsd(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value)
}

function usd(value: unknown): string | undefined {
  const amount = num(value)
  return amount != null ? formatCompactUsd(amount) : undefined
}

const EXPIRATION_DATE_FORMAT = 'MM/DD/YY'

function buildInsuranceStatus(
  expiresAt: dayjs.Dayjs,
): SidebarCarrierInsuranceStatus {
  const endOfExpirationDay = expiresAt.endOf('day')
  return {
    isActive: !endOfExpirationDay.isBefore(dayjs()),
    daysLeft: Math.max(endOfExpirationDay.diff(dayjs(), 'day'), 0),
  }
}

function buildInsurance(
  detail: CarrierResponse,
): SidebarCarrierInsurance | undefined {
  const raw = str(detail.insurance_expiration_date)
  const parsed = raw ? dayjs(raw) : undefined
  const expiresAt = parsed?.isValid() ? parsed : undefined
  const insurance: SidebarCarrierInsurance = {
    status: expiresAt ? buildInsuranceStatus(expiresAt) : undefined,
    expirationDate: expiresAt?.format(EXPIRATION_DATE_FORMAT),
    autoLiability: usd(detail.auto_liability_amount),
    cargo: usd(detail.cargo_insurance_amount),
    cargoDeductible: usd(detail.cargo_insurance_deductible),
    fileUrl: str(detail.insurance_file),
  }
  const hasContent = Object.values(insurance).some((value) => value != null)
  return hasContent ? insurance : undefined
}

function buildIdentifiers(
  brief: CarrierBrief,
  detail: CarrierResponse | undefined,
  fmcsa: FmcsaFields,
) {
  return {
    mc:
      fmcsa.mcNumber ??
      stripMcPrefix(str(detail?.mc_number) ?? str(brief.mc_number)),
    dot: str(detail?.usdot) ?? fmcsa.dotNumber,
  }
}

function buildContact(detail: CarrierResponse | undefined) {
  return {
    contactName: str(detail?.contact_name),
    phone: str(detail?.phone),
    email: str(detail?.email),
  }
}

/**
 * Merges the dispatched carrier brief that always rides on the shipment with
 * the full carrier record from `GET /api/v1/carriers/{carrier_id}` (fetched
 * lazily). The brief guarantees a name and MC even before — or if — the
 * carrier lookup resolves.
 */
export function buildSidebarCarrier(
  brief: CarrierBrief,
  detail: CarrierResponse | undefined,
): SidebarCarrierInfo {
  const fmcsa = buildFmcsaFields(detail)
  return {
    name: str(detail?.name) ?? brief.name,
    isAuthorized: fmcsa.isAuthorized,
    ...buildIdentifiers(brief, detail, fmcsa),
    trucks: fmcsa.trucks,
    drivers: fmcsa.drivers,
    address: detail ? buildAddress(detail) : undefined,
    ...buildContact(detail),
    insurance: detail ? buildInsurance(detail) : undefined,
  }
}
